import { Group, SimpleGrid, Skeleton, Stack, Table, Text, Title, Grid, Pill } from '@mantine/core'
import {
  GetStationDetailsForSpeciResponse,
  SpeciDto,
  SpeciLineDtoInput,
  SpeciType,
} from '@loom-api-types'
import { consignmentStatusValueToString } from 'shared/src/constants/speci-consignment-status'
import { ZenteraLogo } from '@shared/components/zentera-logo/zentera-logo'

interface ViewWoolSpecificationProps {
  stationDetailsForSpeci: GetStationDetailsForSpeciResponse | undefined | null
  stationDataLoading: boolean
  speci?: SpeciDto
  stationRecordNumber?: string
  totalBales?: number
  sentByText?: string
  displayLogo?: boolean
}
export function ViewWoolSpecification({
  stationDetailsForSpeci,
  stationDataLoading,
  speci,
  stationRecordNumber,
  totalBales,
  sentByText,
  displayLogo = false,
}: ViewWoolSpecificationProps) {
  return (
    <Stack>
      {displayLogo && (
        <Title order={1}>
          <ZenteraLogo />
        </Title>
      )}
      <Title order={2} size='h3'>
        Zentera ZQ Specification
      </Title>
      <Grid>
        <Grid.Col span={2}>
          <Title order={5}>Brand:</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Skeleton visible={stationDataLoading}>
            <Text>{stationDetailsForSpeci?.brand || 'N/A'}</Text>
          </Skeleton>
        </Grid.Col>
        <Grid.Col span={2}>
          <Title order={5}>Station ID:</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text>{stationRecordNumber}</Text>
        </Grid.Col>

        <Grid.Col span={2}>
          <Title order={5}>Trading Name:</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Skeleton visible={stationDataLoading}>
            <Text>{stationDetailsForSpeci?.tradingName || 'N/A'}</Text>
          </Skeleton>
        </Grid.Col>
        <Grid.Col span={2}>
          <Title order={5}>Classer:</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text>{speci?.classer}</Text>
        </Grid.Col>

        <Grid.Col span={2}>
          <Title order={5}>Wool store:</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text>{speci?.woolStore || ''}</Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Title order={5}>Breed(s):</Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Group gap='xs'>
            {speci?.breeds.map((breed: string, index: number) => (
              <Pill key={`breed-pill-${breed}-${index}`}>{breed}</Pill>
            ))}
          </Group>
        </Grid.Col>
      </Grid>

      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Line number</Table.Th>
            <Table.Th>Line Description</Table.Th>
            <Table.Th>Traditional description</Table.Th>
            <Table.Th>Bales</Table.Th>
            <Table.Th>Bale Count</Table.Th>
            <Table.Th>Instruction</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {speci?.lines.map((line: SpeciLineDtoInput) => (
            <Table.Tr key={line.id}>
              <Table.Td>{line.number}</Table.Td>
              <Table.Td>{line.description}</Table.Td>
              <Table.Td>{line.traditionalDescription}</Table.Td>
              <Table.Td>
                <Group gap='xs'>
                  {line.baleNumbers.map((value, index) => (
                    <Pill key={`bale-pill-${value}-${index}`}>{value}</Pill>
                  ))}
                </Group>
              </Table.Td>
              <Table.Td>{line.baleNumbers.length}</Table.Td>
              <Table.Td>{line.instructions}</Table.Td>
            </Table.Tr>
          ))}
          <Table.Tr>
            <Table.Td colSpan={4} fw={500}>
              Total
            </Table.Td>
            <Table.Td fw={500}>{totalBales}</Table.Td>
            <Table.Td />
          </Table.Tr>
        </Table.Tbody>
      </Table>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Stack gap='lg'>
          <Title order={5}>Classer comment</Title>
          <Text>{speci?.classerComment}</Text>
        </Stack>
        <Stack gap='lg'>
          <Stack gap='xs'>
            <Title order={5}>Consignment status</Title>
            <Text>
              {speci?.consignmentStatus
                ? consignmentStatusValueToString(speci.consignmentStatus)
                : ''}
            </Text>
          </Stack>
          {speci?.type === SpeciType.Grower && (
            <Stack gap='xs'>
              <Title order={5}>Area Manager</Title>
              <Text>{speci?.stationAreaManagerFullName}</Text>
            </Stack>
          )}
          {sentByText && (
            <Stack gap='xs'>
              <Title order={5}>Sent by</Title>
              <Text>{sentByText}</Text>
            </Stack>
          )}
        </Stack>
      </SimpleGrid>
    </Stack>
  )
}
